import { getSessionParams } from "../features/session/utils/sessionStorage";
import { UserInterfaces } from "../interfaces";
import { apiFetch } from "../shared/utils/http";
import { logger } from "../shared/utils/logger.client";

const interval = 1000;
const maxAttempts = 10;

interface PreviewDetails {
  evidenceURL: string;
  fileName: string;
}

async function getToken() {
  const response = await apiFetch("/session/token", {
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error(
      "Failed to retrieve token. Please try again later.",
    );
  }

  const data = await readJsonSafe(response);

  if (!data) {
    throw new Error(
      "Failed to retrieve token. Please try again later.",
    );
  }

  return {
    token: data.token,
  };
}

async function getUserClaimsFromServer(): Promise<
  UserInterfaces["UserData"]
> {
  const response = await apiFetch("/session/me", {
    credentials: "include",
  });

  if (response.status === 401) {
    throw new Error(
      "Unauthorized. Please sign in again.",
    );
  }

  if (response.status === 404) {
    throw new Error(
      "Document not found. Please try again later.",
    );
  }

  const data = await readJsonSafe(response);

  if (!data) {
    throw new Error(
      "Failed to retrieve user claims. Please try again later.",
    );
  }

  return claimsToUserData(data);
}

async function checkAuthorizationStatus(
  userData: UserInterfaces["UserData"] | undefined,
): Promise<void> {
  const sessionParams = getSessionParams();

  if (!sessionParams?.sessionId) {
    throw new Error(
      "No session ID found. Please sign in again.",
    );
  }

  const { query, body } = buildQueryParams(
    userData,
    sessionParams,
  );

  await pollAuthorizationStatus(query, body);
}

async function pollAuthorizationStatus(
  query: string,
  body: Record<string, unknown>,
  attempt = 0,
): Promise<boolean> {
  function checkStatus(): Promise<Response> {
    return apiFetch(
      `/proxy/checkAuthorized?${query}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(body),
        credentials: "include",
      },
      {
        autoLogoutOn401: false,
        throwOn401: false,
      },
    );
  }

  while (attempt < maxAttempts) {
    const response = await checkStatus();

    if (response.status === 200) {
      return true;
    }

    if ([202, 204, 503, 504].includes(response.status)) {
      attempt += 1;
      await waitBeforeRetry();
      continue;
    }

    if (response.status === 401) {
      throw new Error("consent.error.unauthorizedAccess");
    }

    if (response.status === 404) {
      throw new Error("consent.error.documentNotFound");
    }

    throw new Error("consent.error.unexpected");
  }

  throw new Error("consent.error.authorizationTakingLong");
}

async function getWatermarkedPreview(): Promise<PreviewDetails> {
  const sessionId = getSessionParams().sessionId;

  if (!sessionId) {
    throw new Error(
      "No session ID found. Please sign in again.",
    );
  }

  const query = new URLSearchParams({
    session: sessionId,
  }).toString();

  let attempts = 0;

  while (attempts < maxAttempts) {
    const response = await apiFetch(
      `/proxy/watermarked-preview?${query}`,
      {
        method: "GET",
        headers: {
          Accept: "image/png",
        },
        credentials: "include",
        cache: "no-store",
      },
      {
        autoLogoutOn401: false,
        throwOn401: false,
      },
    );

    if (
      response.status === 202 ||
      response.status === 503 ||
      response.status === 504
    ) {
      attempts += 1;

      logger.info(
        `[Identification] Preview not ready. Attempt ${attempts}/${maxAttempts}.`,
      );

      await waitBeforeRetry();
      continue;
    }

    if (response.status === 401) {
      throw new Error(
        "Unauthorized. Please sign in again.",
      );
    }

    if (response.status === 403) {
      throw new Error(
        "You are not authorised to retrieve this preview.",
      );
    }

    if (response.status === 404) {
      throw new Error(
        "Document not found. Please try again later.",
      );
    }

    if (!response.ok) {
      const responseBody = await readJsonSafe(response);

      logger.error(
        "[Identification] Unexpected preview response.",
        {
          status: response.status,
          responseBody,
        },
      );

      throw new Error(
        "Error fetching preview. Please try again later.",
      );
    }

    const contentType = (
      response.headers.get("content-type") || ""
    ).toLowerCase();

    if (!contentType.includes("image/png")) {
      const responseSample = await response
        .text()
        .catch(() => "");

      logger.error(
        "[Identification] Expected PNG preview but received unexpected content.",
        {
          contentType,
          responseSample: responseSample.slice(0, 200),
        },
      );

      throw new Error(
        "The document preview could not be retrieved.",
      );
    }

    const imageBlob = await response.blob();

    if (imageBlob.size === 0) {
      attempts += 1;

      logger.warn(
        `[Identification] Empty preview image received. Attempt ${attempts}/${maxAttempts}.`,
      );

      await waitBeforeRetry();
      continue;
    }

    const fileName = getPreviewFileName(
      response,
      sessionId,
    );

    return {
      evidenceURL: URL.createObjectURL(imageBlob),
      fileName,
    };
  }

  throw new Error(
    "Preview was not ready after multiple attempts. Please try again later.",
  );
}

function getPreviewFileName(
  response: Response,
  sessionId: string,
): string {
  const contentDisposition = response.headers.get(
    "content-disposition",
  );

  const encodedFileNameMatch = contentDisposition?.match(
    /filename\*=UTF-8''([^;]+)/i,
  );

  if (encodedFileNameMatch?.[1]) {
    try {
      return decodeURIComponent(
        encodedFileNameMatch[1],
      );
    } catch (error) {
      logger.warn(
        "[Identification] Could not decode preview filename.",
        error,
      );
    }
  }

  const fileNameMatch = contentDisposition?.match(
    /filename="?([^";]+)"?/i,
  );

  return (
    fileNameMatch?.[1]?.trim() ||
    `preview-document-${sessionId}.png`
  );
}

function acceptDoc(): Promise<Response> {
  const sessionId = getSessionParams().sessionId;

  if (!sessionId) {
    return Promise.reject(
      new Error(
        "No session ID found. Please sign in again.",
      ),
    );
  }

  const query = new URLSearchParams({
    session: sessionId,
  }).toString();

  return apiFetch(`/proxy/acceptPreview?${query}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
  });
}

async function waitBeforeRetry(): Promise<void> {
  await new Promise<void>((resolve) => {
    window.setTimeout(resolve, interval);
  });
}

const readJsonSafe = async (
  response: Response,
): Promise<any | null> => {
  const contentType = (
    response.headers.get("content-type") || ""
  ).toLowerCase();

  if (!contentType.includes("application/json")) {
    logger.warn(
      `[Authorization] Expected JSON but got content-type="${contentType}" (possible SPA fallback)`,
    );

    return null;
  }

  if (response.status === 204) {
    return null;
  }

  try {
    return await response.json();
  } catch (error) {
    logger.error(
      "[Authorization] JSON parse failed:",
      error,
    );

    return null;
  }
};

function buildQueryParams(
  userData: UserInterfaces["UserData"] | undefined,
  sessionParams: ReturnType<typeof getSessionParams>,
) {
  const query = new URLSearchParams({
    session: sessionParams.sessionId ?? "",
    identityMatchingEnabled: "true",
  }).toString();

  const body = {
    email: userData?.email,
    givenName: userData?.firstName,
    firstName: userData?.firstName,
    lastName: userData?.lastName,
    ppsn: userData?.ppsn,
    birthDate: toDDMMYYYY(userData?.birthDate),
  };

  return {
    query,
    body,
  };
}

function isDateDDMMYYYY(date: string): boolean {
  return /^\d{2}\/\d{2}\/\d{4}$/.test(date);
}

function toDDMMYYYY(
  input: string | Date | undefined,
): string {
  if (!input) {
    return "";
  }

  if (input instanceof Date) {
    if (Number.isNaN(input.getTime())) {
      return "";
    }

    const day = String(input.getDate()).padStart(
      2,
      "0",
    );

    const month = String(
      input.getMonth() + 1,
    ).padStart(2, "0");

    const year = input.getFullYear();

    return `${day}/${month}/${year}`;
  }

  if (isDateDDMMYYYY(input)) {
    return input;
  }

  const parsedDate = new Date(input);

  if (Number.isNaN(parsedDate.getTime())) {
    return input;
  }

  const day = String(parsedDate.getDate()).padStart(
    2,
    "0",
  );

  const month = String(
    parsedDate.getMonth() + 1,
  ).padStart(2, "0");

  const year = parsedDate.getFullYear();

  return `${day}/${month}/${year}`;
}

function toDateOrUndefined(
  input: string | Date | undefined,
): Date | undefined {
  if (!input) {
    return undefined;
  }

  if (input instanceof Date) {
    return Number.isNaN(input.getTime())
      ? undefined
      : input;
  }

  const parsedDate = new Date(input);

  return Number.isNaN(parsedDate.getTime())
    ? undefined
    : parsedDate;
}

function claimsToUserData(
  claims: UserInterfaces["UserClaims"],
): UserInterfaces["UserData"] {
  return {
    email: claims.email ?? "",
    firstName:
      claims.firstName ??
      claims.given_name ??
      "",
    lastName:
      claims.lastName ??
      claims.family_name ??
      "",
    ppsn:
      claims.PublicServiceNumber ??
      (claims as any).PPSN ??
      (claims as any).ppsn ??
      (claims as any).sub ??
      "",
    birthDate: toDateOrUndefined(
      claims.BirthDate ??
        claims.birthdate ??
        claims.dob,
    ),
  };
}

export const identificationService = {
  checkAuthorizationStatus,
  getUserClaimsFromServer,
  getToken,
  getWatermarkedPreview,
  acceptDoc,
};